import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-sand border border-stone-300 focus:border-forest focus:ring-1 focus:ring-forest rounded-xl px-4 py-3 text-stone-900 placeholder:text-stone-400 transition-colors";

function Field({ label, id, required, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium text-stone-800">
        {label} {required && <span className="text-terracotta" aria-hidden="true">*</span>}
      </label>
      {children}
    </div>
  );
}

export default function InquiryForm({
  kind,
  variant = "event",
  eventOptions = [],
  submitLabel,
  prefilledSubject = "",
  testIdPrefix,
}) {
  const isEvent = variant === "event";
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    preferred_date: "",
    guests: "",
    event_type: "",
    subject: prefilledSubject,
    message: "",
    privacy_accepted: false,
    website: "",
  });
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/inquiries`, {
        kind,
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        preferred_date: isEvent && form.preferred_date ? form.preferred_date : null,
        guests: isEvent && form.guests ? Number(form.guests) : null,
        event_type: isEvent && form.event_type ? form.event_type : null,
        subject: !isEvent && form.subject ? form.subject : null,
        message: form.message || null,
        privacy_accepted: form.privacy_accepted,
        website: form.website,
      });
      toast.success("Richiesta inviata. Ti risponderemo presto, promesso.");
      setForm({ name: "", email: "", phone: "", preferred_date: "", guests: "", event_type: "", subject: "", message: "", privacy_accepted: false, website: "" });
    } catch (err) {
      if (err?.response?.status === 429) {
        toast.error("Hai inviato troppe richieste. Riprova tra qualche minuto.");
      } else {
        toast.error("Qualcosa è andato storto. Riprova tra poco.");
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid={`${testIdPrefix}-form`} noValidate={false}>
      <Field label="Nome e cognome" id={`${testIdPrefix}-name`} required>
        <input id={`${testIdPrefix}-name`} data-testid={`${testIdPrefix}-name-input`} type="text" required minLength={2} maxLength={100}
          className={inputCls} value={form.name} onChange={set("name")} placeholder="Come ti chiami?" autoComplete="name" />
      </Field>
      <Field label="Email" id={`${testIdPrefix}-email`} required>
        <input id={`${testIdPrefix}-email`} data-testid={`${testIdPrefix}-email-input`} type="email" required
          className={inputCls} value={form.email} onChange={set("email")} placeholder="nome@esempio.it" autoComplete="email" />
      </Field>
      <Field label="Telefono (facoltativo)" id={`${testIdPrefix}-phone`}>
        <input id={`${testIdPrefix}-phone`} data-testid={`${testIdPrefix}-phone-input`} type="tel" maxLength={30}
          className={inputCls} value={form.phone} onChange={set("phone")} placeholder="Se preferisci una chiamata" autoComplete="tel" />
      </Field>
      {isEvent ? (
        <>
          <Field label="Data preferita" id={`${testIdPrefix}-date`}>
            <input id={`${testIdPrefix}-date`} data-testid={`${testIdPrefix}-date-input`} type="date"
              min={new Date().toISOString().split("T")[0]}
              className={inputCls} value={form.preferred_date} onChange={set("preferred_date")} />
          </Field>
          <Field label="Numero di persone" id={`${testIdPrefix}-guests`}>
            <input id={`${testIdPrefix}-guests`} data-testid={`${testIdPrefix}-guests-input`} type="number" min={1} max={200}
              className={inputCls} value={form.guests} onChange={set("guests")} placeholder="Es. 6" />
          </Field>
          <Field label="Tipo di evento" id={`${testIdPrefix}-type`}>
            <select id={`${testIdPrefix}-type`} data-testid={`${testIdPrefix}-type-select`}
              className={inputCls} value={form.event_type} onChange={set("event_type")}>
              <option value="">Scegli…</option>
              {eventOptions.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
          </Field>
        </>
      ) : (
        <Field label="Oggetto" id={`${testIdPrefix}-subject`}>
          <input id={`${testIdPrefix}-subject`} data-testid={`${testIdPrefix}-subject-input`} type="text" maxLength={150}
            className={inputCls} value={form.subject} onChange={set("subject")} placeholder="Di cosa parliamo?" />
        </Field>
      )}
      <div className="sm:col-span-2">
        <Field label="Messaggio" id={`${testIdPrefix}-message`}>
          <textarea id={`${testIdPrefix}-message`} data-testid={`${testIdPrefix}-message-input`} rows={5} maxLength={2000}
            className={inputCls} value={form.message} onChange={set("message")}
            placeholder="Raccontaci cosa hai in mente: date, gusti, intolleranze, idee strane. Vale tutto." />
        </Field>
      </div>
      <div className="hidden" aria-hidden="true">
        <label htmlFor={`${testIdPrefix}-website`}>Non compilare questo campo</label>
        <input id={`${testIdPrefix}-website`} type="text" tabIndex={-1} autoComplete="off"
          value={form.website} onChange={set("website")} />
      </div>
      <div className="sm:col-span-2 flex items-start gap-3">
        <input id={`${testIdPrefix}-privacy`} data-testid={`${testIdPrefix}-privacy-checkbox`} type="checkbox" required
          checked={form.privacy_accepted} onChange={set("privacy_accepted")}
          className="mt-1 h-4 w-4 rounded border-stone-300 accent-[#2D4A3E]" />
        <label htmlFor={`${testIdPrefix}-privacy`} className="text-sm text-stone-700 leading-relaxed">
          Ho letto la <Link to="/privacy-policy" className="underline underline-offset-2 text-forest font-medium">Privacy Policy</Link> e
          acconsento al trattamento dei miei dati per essere ricontattato. <span className="text-terracotta" aria-hidden="true">*</span>
        </label>
      </div>
      <div className="sm:col-span-2">
        <button type="submit" disabled={sending} data-testid={`${testIdPrefix}-submit-button`}
          className="inline-flex items-center gap-2 bg-terracotta hover:bg-terracotta-dark text-white font-medium px-7 py-3.5 rounded-full transition-all shadow-sm hover:shadow-md active:scale-95 disabled:opacity-60 disabled:pointer-events-none">
          {sending ? <Loader2 size={18} className="animate-spin" aria-hidden="true" /> : <Send size={18} aria-hidden="true" />}
          {submitLabel}
        </button>
        <p className="mt-3 text-xs text-stone-500">
          Nessuna newsletter, nessun remarketing. Usiamo i tuoi dati solo per risponderti.
        </p>
      </div>
    </form>
  );
}
